"use client";

import { useMemo } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip } from "recharts";
import { cn } from "@/lib/utils";

interface TrendChartProps {
  data: number[]; // Simple array of values for sparkline
  className?: string;
  trend?: "up" | "down" | "neutral";
  height?: number;
  showTooltip?: boolean;
  lineColor?: string;
  fillColor?: string;
}

export function TrendChart({
  data,
  className,
  trend,
  height = 40,
  showTooltip = false,
  lineColor,
  fillColor,
}: TrendChartProps) {
  // Determine colors based on trend if not provided
  const colors = useMemo(() => {
    if (lineColor && fillColor) return { line: lineColor, fill: fillColor };

    if (trend === "up") return { line: "#10b981", fill: "#10b981" }; // green-500
    if (trend === "down") return { line: "#ef4444", fill: "#ef4444" }; // red-500
    if (trend === "neutral") return { line: "#eab308", fill: "#eab308" }; // yellow-500

    // Default blue
    return { line: "#3b82f6", fill: "#3b82f6" };
  }, [trend, lineColor, fillColor]);

  // Transform number array to object array for Recharts
  const chartData = useMemo(() => {
    return data.map((val, i) => ({ i, value: val }));
  }, [data]);

  if (!data || data.length === 0) return null;

  return (
    <div className={cn("w-full overflow-hidden", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData}>
          <defs>
            <linearGradient
              id={`gradient-${trend || "default"}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="5%" stopColor={colors.fill} stopOpacity={0.3} />
              <stop offset="95%" stopColor={colors.fill} stopOpacity={0} />
            </linearGradient>
          </defs>
          {showTooltip && <Tooltip />}
          <Area
            type="monotone"
            dataKey="value"
            stroke={colors.line}
            strokeWidth={2}
            fill={`url(#gradient-${trend || "default"})`}
            isAnimationActive={false} // Disable animation for pure sparklines usage usually
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
